"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  isSatSection,
  isSatTotal,
  SAT_SECTION_ROWS,
  SAT_TOTAL_ROWS,
  type SatGroup,
  type SatPercentile,
  satLowestTotalForPercentile,
  satSectionRow,
  satTotalRow,
} from "@/lib/tool-kit/exam-scores/sat-percentiles";

/** English strings. A translated content file overrides these through `ui`. */
export const SAT_PERCENTILE_CALCULATOR_STRINGS = {
  inputHeading: "Your SAT scores",
  inputHint:
    "Each section is scored from 200 to 800 in steps of 10; the total runs from 400 to 1600.",
  rwLabel: "Reading and Writing (200–800)",
  mathLabel: "Math (200–800)",
  totalOnlyLabel: "Or enter a total score instead (400–1600)",
  invalidSection: "{section} must be a score from 200 to 800 in steps of 10.",
  invalidTotal: "A total score runs from 400 to 1600 in steps of 10.",
  sections: {
    readingWriting: "Reading and Writing",
    math: "Math",
  },
  results: "Percentile ranks",
  total: "Total score",
  groups: {
    nationally: "Nationally representative percentile",
    user: "SAT user percentile",
  },
  cardLabel: "{measure} — {group}",
  working: "{rw} + {math} = {total}",
  rankLine:
    "A percentile rank is the percentage of students with scores equal to or lower than yours. Nationally representative percentiles weight a sample of all U.S. 11th and 12th graders, whether or not they take the SAT; user percentiles use the actual scores of students who graduated in the past three school years.",
  enter:
    "Enter both section scores, or a total score, to see percentile ranks.",
  targetHeading: "Work backward from a percentile",
  targetLabel: "Percentile you want (1–99)",
  targetResult:
    "{group}: the lowest total with a percentile of at least {target} is {score}.",
  targetNone: "{group}: no total reaches that percentile.",
  targetInvalid: "Enter a whole number from 1 to 99.",
  notation:
    "College Board prints 99+ at the top of the scale and 1- at the bottom, and this page shows them as printed.",
  tableHeading: "College Board percentile tables",
  colTotal: "Total score",
  colSection: "Section score",
  colNational: "Nationally representative",
  colUser: "User group",
  colRwNational: "Reading and Writing, nationally representative",
  colRwUser: "Reading and Writing, user group",
  colMathNational: "Math, nationally representative",
  colMathUser: "Math, user group",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "SAT {total}: nationally representative percentile {national}, user percentile {user} (College Board)",
} as const;

type Strings = typeof SAT_PERCENTILE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const GROUPS: readonly SatGroup[] = ["nationally", "user"];
const SCORE = { useGrouping: false } as const;

function read(raw: string): number | null {
  return raw.trim() === "" ? null : parseNumberInput(raw);
}

/** A percentile as College Board prints it, with the number localised. */
function show(cell: SatPercentile, fmt: Format): string {
  const number = fmt(cell.value);
  if (cell.qualifier === "plus") {
    return `${number}+`;
  }
  return cell.qualifier === "minus" ? `${number}-` : number;
}

function TargetPanel({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const [raw, setRaw] = useState("90");
  const value = read(raw);
  const valid =
    value !== null && Number.isInteger(value) && value >= 1 && value <= 99;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.targetHeading}</h3>
      <TextField
        id="sat-target"
        label={strings.targetLabel}
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div aria-live="polite" className="space-y-1 text-foreground text-sm">
        {valid
          ? GROUPS.map((group) => {
              const score = satLowestTotalForPercentile(group, value);
              return (
                <p key={group}>
                  {score === null
                    ? t(strings.targetNone, { group: strings.groups[group] })
                    : t(strings.targetResult, {
                        group: strings.groups[group],
                        target: fmt(value),
                        score: fmt(score, SCORE),
                      })}
                </p>
              );
            })
          : null}
        {valid || raw.trim() === "" ? null : (
          <Notice>{strings.targetInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

function Tables({ strings, fmt }: { strings: Strings; fmt: Format }) {
  return (
    <section className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.notation}</p>
      <DataTable
        head={[strings.colTotal, strings.colNational, strings.colUser]}
        rows={SAT_TOTAL_ROWS.map((row) => [
          fmt(row.score, SCORE),
          show(row.nationally, fmt),
          show(row.user, fmt),
        ])}
      />
      <DataTable
        head={[
          strings.colSection,
          strings.colRwNational,
          strings.colRwUser,
          strings.colMathNational,
          strings.colMathUser,
        ]}
        rows={SAT_SECTION_ROWS.map((row) => [
          fmt(row.score, SCORE),
          show(row.readingWriting.nationally, fmt),
          show(row.readingWriting.user, fmt),
          show(row.math.nationally, fmt),
          show(row.math.user, fmt),
        ])}
      />
    </section>
  );
}

function PercentileCards({
  measure,
  nationally,
  user,
  strings,
  fmt,
  emphasis = false,
}: {
  measure: string;
  nationally: SatPercentile;
  user: SatPercentile;
  strings: Strings;
  fmt: Format;
  emphasis?: boolean;
}) {
  return (
    <>
      <StatCard
        emphasis={emphasis}
        label={t(strings.cardLabel, {
          measure,
          group: strings.groups.nationally,
        })}
        value={show(nationally, fmt)}
      />
      <StatCard
        emphasis={emphasis}
        label={t(strings.cardLabel, { measure, group: strings.groups.user })}
        value={show(user, fmt)}
      />
    </>
  );
}

export default function SatPercentileCalculatorWidget() {
  const strings = useToolUi(SAT_PERCENTILE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [rwRaw, setRwRaw] = useState("700");
  const [mathRaw, setMathRaw] = useState("690");
  const [totalRaw, setTotalRaw] = useState("");
  const rw = read(rwRaw);
  const math = read(mathRaw);
  const enteredTotal = read(totalRaw);
  const sectionsOk = isSatSection(rw) && isSatSection(math);
  let total: number | null = null;
  if (sectionsOk) {
    total = rw + math;
  } else if (isSatTotal(enteredTotal)) {
    total = enteredTotal;
  }
  const totalRow = total === null ? null : satTotalRow(total);
  const rwRow = isSatSection(rw) ? satSectionRow(rw) : null;
  const mathRow = isSatSection(math) ? satSectionRow(math) : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputHint}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="sat-rw"
          label={strings.rwLabel}
          numeric
          onChange={setRwRaw}
          value={rwRaw}
        />
        <TextField
          id="sat-math"
          label={strings.mathLabel}
          numeric
          onChange={setMathRaw}
          value={mathRaw}
        />
      </div>
      <TextField
        id="sat-total"
        label={strings.totalOnlyLabel}
        numeric
        onChange={setTotalRaw}
        value={totalRaw}
      />

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {rwRaw.trim() !== "" && !isSatSection(rw) ? (
          <Notice>
            {t(strings.invalidSection, {
              section: strings.sections.readingWriting,
            })}
          </Notice>
        ) : null}
        {mathRaw.trim() !== "" && !isSatSection(math) ? (
          <Notice>
            {t(strings.invalidSection, { section: strings.sections.math })}
          </Notice>
        ) : null}
        {totalRaw.trim() !== "" && !isSatTotal(enteredTotal) ? (
          <Notice>{strings.invalidTotal}</Notice>
        ) : null}
        {totalRow && total !== null ? (
          <>
            <StatGrid>
              <StatCard label={strings.total} value={fmt(total, SCORE)} />
              <PercentileCards
                emphasis
                fmt={fmt}
                measure={strings.total}
                nationally={totalRow.nationally}
                strings={strings}
                user={totalRow.user}
              />
              {rwRow ? (
                <PercentileCards
                  fmt={fmt}
                  measure={strings.sections.readingWriting}
                  nationally={rwRow.readingWriting.nationally}
                  strings={strings}
                  user={rwRow.readingWriting.user}
                />
              ) : null}
              {mathRow ? (
                <PercentileCards
                  fmt={fmt}
                  measure={strings.sections.math}
                  nationally={mathRow.math.nationally}
                  strings={strings}
                  user={mathRow.math.user}
                />
              ) : null}
            </StatGrid>
            {sectionsOk ? (
              <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
                {t(strings.working, {
                  rw: fmt(rw, SCORE),
                  math: fmt(math, SCORE),
                  total: fmt(total, SCORE),
                })}
              </p>
            ) : null}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                total: fmt(total, SCORE),
                national: show(totalRow.nationally, fmt),
                user: show(totalRow.user, fmt),
              })}
            />
          </>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
        <p className="text-muted-foreground text-sm">{strings.rankLine}</p>
      </section>

      <TargetPanel fmt={fmt} strings={strings} />
      <Tables fmt={fmt} strings={strings} />
    </div>
  );
}
